import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Flex, Image, Stack } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'

import { isApiError } from '@/types/apiErrors.types'

import { getOnboardingDefaultValues } from '@/modules/onboarding/pages/Onboarding.values'

import { useUserStore } from '@/store/user.store'

import { Progress } from '@/components/Progress/Progress'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import { FORM_MESSAGES_MAP } from '@/constants/formMessages.constants'

import {
  useAdvisorServiceAdvisorControllerGetAllAdvisors,
  useOnboardingServiceOnboardingControllerGetAnswers,
  useOnboardingServiceOnboardingControllerGetAnswersKey,
  useOnboardingServiceOnboardingControllerUpdateAnswers,
} from '@/__generated__/queries'
import type { Answer } from '@/__generated__/requests'

import { OnboardingForm } from '../components/OnboardingForm'
import { onboardingFormSchema } from '../components/OnboardingForm.schema'
import type { OnboardingFormSchema } from '../components/OnboardingForm.types'
import { onboardingFormDefaultValues } from '../components/OnboardingForm.values'
import { steps } from '../source/steps'
import classes from './Onboarding.module.css'

export function EditComponent() {
  const stepsCount = steps.length
  const [currentStep, setCurrentStep] = useState(0)
  const [answers, setAnswers] = useState<Answer[]>([])

  const setUser = useUserStore.use.setUser()

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const location = useLocation()
  const [shouldHighlightFinancial] = useState(() =>
    Boolean(
      (location.state as { highlightFinancial?: boolean } | null)
        ?.highlightFinancial,
    ),
  )

  const { data } = useOnboardingServiceOnboardingControllerGetAnswers()

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [useOnboardingServiceOnboardingControllerGetAnswersKey],
    })
  }, [queryClient])

  const getProgressValue = useCallback(() => {
    switch (currentStep) {
      case 0:
        return 2
      default:
        return (currentStep / stepsCount) * 100
    }
  }, [currentStep, stepsCount])

  const form = useForm<OnboardingFormSchema>({
    initialValues: onboardingFormDefaultValues,
    validate: zodResolver(onboardingFormSchema),
  })

  const onboardingMutation =
    useOnboardingServiceOnboardingControllerUpdateAnswers({
      onError: error => {
        let errorMessage = FORM_MESSAGES_MAP.UNKNOWN_ERROR

        if (isApiError(error)) {
          errorMessage = error.body.message || errorMessage
        }

        notifications.show({
          color: 'red',
          message: errorMessage,
          title: 'Failed to send the onboarding form',
        })
      },
      onSuccess: data => {
        setUser(data)

        modals.open({
          centered: true,
          children: (
            <Stack p={20}>
              <Image
                alt="Rolan Wealth"
                className={classes.contentLogo}
                h="auto"
                src="success.png"
                w={80}
              />
              <Title ta="center" variant="h2">
                Onboarding updated!
              </Title>
              <Text ta="center" variant="body_2">
                Step into your financial future as we guide you to your
                personalized dashboard, filled with insights and resources
                tailored just for you.
              </Text>
            </Stack>
          ),
        })

        navigate(-1)

        queryClient.invalidateQueries({
          queryKey: [useAdvisorServiceAdvisorControllerGetAllAdvisors],
        })
      },
    })

  const finishStep = (stepAnswers: Answer[]) => {
    let newAnswers = [...answers]

    stepAnswers.forEach(stepAnswer => {
      newAnswers = newAnswers.map(answer => {
        if (answer.fieldName === stepAnswer.fieldName) return stepAnswer

        return answer
      })
    })

    let stepHasErrors = false

    steps[currentStep].fields.forEach(field => {
      if (form.validateField(field.field_name).hasError) {
        stepHasErrors = true
      }
    })

    if (!stepHasErrors) {
      if (currentStep < stepsCount - 1) {
        setCurrentStep(currentStep + 1)
      } else {
        onboardingMutation.mutate({
          requestBody: {
            answers: [...newAnswers],
          },
        })
      }
    }

    setAnswers(newAnswers)
  }

  const previousStep = (stepAnswers: Answer[]) => {
    let newAnswers = [...answers]

    stepAnswers.forEach(stepAnswer => {
      newAnswers = newAnswers.map(answer => {
        if (answer.fieldName === stepAnswer.fieldName) return stepAnswer

        return answer
      })
    })

    setAnswers(newAnswers)
    setCurrentStep(currentStep - 1)
  }

  useEffect(() => {
    setAnswers(data?.answers ?? getOnboardingDefaultValues())
  }, [data])

  return (
    <Flex justify="center">
      <Flex className={classes.content}>
        <Progress mb={16} value={getProgressValue()} />

        <OnboardingForm
          key={currentStep}
          currentStep={currentStep}
          finishStep={finishStep}
          highlightFinancial={shouldHighlightFinancial}
          isFinal={currentStep === stepsCount - 1}
          prevAnswers={answers}
          previousStep={previousStep}
          step={steps[currentStep]}
        />
      </Flex>
    </Flex>
  )
}

EditComponent.displayName = 'OnboardingEdit'
